import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';

import { Button } from '../ui/Button';
import { useCreateSubscription } from '../../hooks/useCreateSubscription';
import { useLockScroll } from '../../hooks/useLockScroll';
import { useUpdateSubscription } from '../../hooks/useUpdateSubscription';
import type { CompanySubscription } from '../../hooks/useCompanySubscriptions';
import type { Database } from '../../types/supabase';

interface CreateSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string | undefined;
  subscription?: CompanySubscription | null;
  onSuccess?: () => void;
}

const SUBSCRIPTION_TYPE_LABELS: Record<string, string> = {
  recurring: 'Recurring',
  punch_card: 'Punch Card',
  trial: 'Trial',
  drop_in: 'Drop-In',
};

const ACCESS_TYPE_LABELS: Record<string, string> = {
  days: 'Days',
  classes: 'Classes',
  unlimited: 'Unlimited',
};

const getInitialForm = (sub?: CompanySubscription | null) => {
  if (!sub) {
    return {
      name: '',
      price: '',
      subscriptionType: 'recurring' as const,
      accessType: 'unlimited' as const,
      accessValue: '',
      trialDurationType: 'days' as const,
      trialDurationDays: '',
      trialDurationClasses: '',
      trialDurationPeriod: '30 days',
      contractTerm: 'monthly',
      billingCycle: 'monthly',
      prepaymentRequired: false,
      prepaymentMonths: '',
      isFreeTrial: false,
      requiresApproval: true,
      allowsCashPayment: false,
    };
  }
  return {
    name: sub.name,
    price: sub.price.toString(),
    subscriptionType: sub.subscription_type as 'recurring' | 'punch_card' | 'trial' | 'drop_in',
    accessType: sub.access_type as 'days' | 'classes' | 'unlimited',
    accessValue: sub.access_type === 'unlimited' ? '' : sub.access_value.toString(),
    trialDurationType: (sub.trial_duration_days ? 'days' : sub.trial_duration_classes ? 'classes' : sub.trial_duration_period ? 'period' : 'days') as 'days' | 'classes' | 'period',
    trialDurationDays: sub.trial_duration_days?.toString() ?? '',
    trialDurationClasses: sub.trial_duration_classes?.toString() ?? '',
    trialDurationPeriod: sub.trial_duration_period ?? '30 days',
    contractTerm: sub.contract_term,
    billingCycle: sub.billing_cycle,
    prepaymentRequired: sub.prepayment_required,
    prepaymentMonths: sub.prepayment_months?.toString() ?? '',
    isFreeTrial: sub.is_free_trial,
    requiresApproval: sub.requires_approval,
    allowsCashPayment: sub.allows_cash_payment,
  };
};

/**
 * Create/Edit Subscription Modal - Creates or updates company_subscriptions for gym plans.
 */
export function CreateSubscriptionModal({
  isOpen,
  onClose,
  companyId,
  subscription,
  onSuccess,
}: CreateSubscriptionModalProps) {
  const createSubscription = useCreateSubscription();
  const updateSubscription = useUpdateSubscription();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(getInitialForm(null));

  useEffect(() => {
    if (isOpen) {
      setForm(getInitialForm(subscription));
      setError(null);
    }
  }, [isOpen, subscription]);

  const handleClose = () => {
    setError(null);
    setForm(getInitialForm(null));
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subscription && !companyId) return;

    setError(null);

    if (!form.name.trim()) {
      setError('Subscription name is required');
      return;
    }
    if (!form.price && !form.isFreeTrial) {
      setError('Price is required');
      return;
    }
    if (
      form.prepaymentRequired &&
      (!form.prepaymentMonths || parseInt(form.prepaymentMonths, 10) <= 0)
    ) {
      setError('Pre-payment months must be greater than 0 when pre-payment is required');
      return;
    }
    if (form.subscriptionType === 'trial') {
      const hasDays =
        form.trialDurationType === 'days' &&
        form.trialDurationDays &&
        parseInt(form.trialDurationDays, 10) > 0;
      const hasClasses =
        form.trialDurationType === 'classes' &&
        form.trialDurationClasses &&
        parseInt(form.trialDurationClasses, 10) > 0;
      const hasPeriod =
        form.trialDurationType === 'period' && form.trialDurationPeriod;
      if (!hasDays && !hasClasses && !hasPeriod) {
        setError('Please specify trial duration (days, classes, or period)');
        return;
      }
    }

    const basePayload = {
      name: form.name.trim(),
      price: form.isFreeTrial ? 0 : parseFloat(form.price) || 0,
      subscription_type: form.subscriptionType,
      access_type: form.accessType,
      access_value:
        form.accessType === 'unlimited'
          ? 0
          : parseInt(form.accessValue, 10) || 0,
      contract_term: form.contractTerm,
      billing_cycle: form.billingCycle,
      prepayment_required: form.prepaymentRequired,
      prepayment_months: form.prepaymentRequired
        ? parseInt(form.prepaymentMonths, 10) || 0
        : 0,
      is_free_trial: form.isFreeTrial,
      requires_approval: form.requiresApproval,
      allows_cash_payment: form.allowsCashPayment,
    };

    if (form.subscriptionType === 'trial') {
      if (form.trialDurationType === 'days') {
        (basePayload as Record<string, unknown>).trial_duration_days =
          parseInt(form.trialDurationDays, 10) || null;
        (basePayload as Record<string, unknown>).trial_duration_classes = null;
        (basePayload as Record<string, unknown>).trial_duration_period = null;
      } else if (form.trialDurationType === 'classes') {
        (basePayload as Record<string, unknown>).trial_duration_classes =
          parseInt(form.trialDurationClasses, 10) || null;
        (basePayload as Record<string, unknown>).trial_duration_days = null;
        (basePayload as Record<string, unknown>).trial_duration_period = null;
      } else {
        (basePayload as Record<string, unknown>).trial_duration_period =
          form.trialDurationPeriod || null;
        (basePayload as Record<string, unknown>).trial_duration_days = null;
        (basePayload as Record<string, unknown>).trial_duration_classes = null;
      }
    } else {
      (basePayload as Record<string, unknown>).trial_duration_days = null;
      (basePayload as Record<string, unknown>).trial_duration_classes = null;
      (basePayload as Record<string, unknown>).trial_duration_period = null;
    }

    if (subscription) {
      updateSubscription.mutate(
        {
          id: subscription.id,
          ...basePayload,
        },
        {
          onSuccess: () => {
            handleClose();
            onSuccess?.();
          },
          onError: (err: Error) => {
            setError(err.message || 'Failed to update subscription');
          },
        },
      );
    } else {
      if (!companyId) return;
      const insertData: Database['public']['Tables']['company_subscriptions']['Insert'] =
        {
          company_id: companyId,
          ...basePayload,
          sports_included: [],
          amenities_included: [],
        };
      createSubscription.mutate(insertData, {
        onSuccess: () => {
          handleClose();
          onSuccess?.();
        },
        onError: (err: Error) => {
          setError(err.message || 'Failed to create subscription');
        },
      });
    }
  };

  if (!isOpen) return null;

  const isEditing = Boolean(subscription);
  const isPending = createSubscription.isPending || updateSubscription.isPending;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-[70] transition-opacity"
        onClick={handleClose}
        aria-hidden
      />

      <div
        className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        role="dialog"
        aria-label={isEditing ? 'Edit subscription' : 'Create subscription'}
        onClick={handleClose}
      >
        <div
          className="w-full max-w-xl max-h-[90vh] bg-[#1a1818] border border-[#504A4A] rounded-xl shadow-2xl flex flex-col animate-in zoom-in-95 fade-in-0 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-[#504A4A] shrink-0">
            <h2 className="text-lg font-semibold text-[#EBFFEE]">
              {isEditing ? 'Edit Subscription' : 'Create Subscription'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              className="p-2 rounded-lg text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-white/5 transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0 flex flex-col"
          >
            {error && (
              <div className="p-3 rounded-lg bg-red-900/20 border border-red-500/50 text-sm text-red-400">
                {error}
              </div>
            )}

            {/* SECTION 1: Basic Info */}
            <section>
              <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">
                Basic Info
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-[#A9A3A3] mb-2">
                    Subscription Name <span className="text-[#9B1421]">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="e.g., Basic Monthly Plan"
                    maxLength={100}
                    className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#A9A3A3] mb-2">
                    Subscription Type
                  </label>
                  <div className="flex rounded-lg bg-[#221F1F] p-1 border border-[#504A4A]">
                    {(
                      ['recurring', 'punch_card', 'trial', 'drop_in'] as const
                    ).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() =>
                          setForm((f) => ({ ...f, subscriptionType: type }))
                        }
                        className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors border-2 ${
                          form.subscriptionType === type
                            ? 'border-white bg-white/5 text-[#EBFFEE]'
                            : 'border-transparent text-[#A9A3A3] hover:text-[#EBFFEE]'
                        }`}
                      >
                        {SUBSCRIPTION_TYPE_LABELS[type]}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[#A9A3A3] mb-2">
                    Price (USD)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]">
                      $
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={form.price}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, price: e.target.value }))
                      }
                      placeholder="99.00"
                      disabled={form.isFreeTrial}
                      className="w-full pl-8 pr-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700 disabled:opacity-50"
                    />
                  </div>
                  {form.subscriptionType === 'trial' && (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.isFreeTrial}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            isFreeTrial: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 rounded border-[#504A4A] text-green-700"
                      />
                      <span className="text-sm text-[#A9A3A3]">
                        Free trial
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </section>

            {/* SECTION 2: Access Level */}
            <section>
              <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">
                Access Level
              </h3>
              <div className="flex rounded-lg bg-[#221F1F] p-1 border border-[#504A4A]">
                {(['days', 'classes', 'unlimited'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        accessType: type,
                        accessValue: type === 'unlimited' ? '' : f.accessValue,
                      }))
                    }
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors border-2 ${
                      form.accessType === type
                        ? 'border-white bg-white/5 text-[#EBFFEE]'
                        : 'border-transparent text-[#A9A3A3] hover:text-[#EBFFEE]'
                    }`}
                  >
                    {ACCESS_TYPE_LABELS[type]}
                  </button>
                ))}
              </div>
              {form.accessType !== 'unlimited' && (
                <input
                  type="number"
                  value={form.accessValue}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, accessValue: e.target.value }))
                  }
                  placeholder={`Number of ${form.accessType}`}
                  min={1}
                  className="w-full mt-3 px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                />
              )}
            </section>

            {/* SECTION 3: Trial Duration (Trial only) */}
            {form.subscriptionType === 'trial' && (
              <section>
                <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">
                  Trial Duration
                </h3>
                <div className="flex rounded-lg bg-[#221F1F] p-1 border border-[#504A4A] mb-3">
                  {(['days', 'classes', 'period'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({ ...f, trialDurationType: type }))
                      }
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors border-2 ${
                        form.trialDurationType === type
                          ? 'border-white bg-white/5 text-[#EBFFEE]'
                          : 'border-transparent text-[#A9A3A3] hover:text-[#EBFFEE]'
                      }`}
                    >
                      {type === 'days'
                        ? 'Days'
                        : type === 'classes'
                          ? 'Classes'
                          : 'Period'}
                    </button>
                  ))}
                </div>
                {form.trialDurationType === 'days' && (
                  <input
                    type="number"
                    value={form.trialDurationDays}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        trialDurationDays: e.target.value,
                      }))
                    }
                    placeholder="e.g., 30"
                    min={1}
                    className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                  />
                )}
                {form.trialDurationType === 'classes' && (
                  <input
                    type="number"
                    value={form.trialDurationClasses}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        trialDurationClasses: e.target.value,
                      }))
                    }
                    placeholder="e.g., 5"
                    min={1}
                    className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                  />
                )}
                {form.trialDurationType === 'period' && (
                  <div className="flex flex-wrap gap-2">
                    {['7 days', '14 days', '30 days', '60 days', '90 days'].map(
                      (period) => (
                        <button
                          key={period}
                          type="button"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              trialDurationPeriod: period,
                            }))
                          }
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            form.trialDurationPeriod === period
                              ? 'bg-green-700/20 border border-green-700 text-green-700'
                              : 'bg-[#221F1F] border border-[#504A4A] text-[#A9A3A3] hover:text-[#EBFFEE]'
                          }`}
                        >
                          {period}
                        </button>
                      ),
                    )}
                  </div>
                )}
              </section>
            )}

            {/* SECTION 4: Billing (Recurring only) */}
            {form.subscriptionType === 'recurring' && (
              <section>
                <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">
                  Billing
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-[#A9A3A3] mb-2">
                      Contract Term
                    </label>
                    <select
                      value={form.contractTerm}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          contractTerm: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] text-sm focus:outline-none focus:border-green-700 appearance-none cursor-pointer"
                    >
                      <option value="monthly" className="bg-[#221F1F]">
                        Monthly
                      </option>
                      <option value="quarterly" className="bg-[#221F1F]">
                        Quarterly
                      </option>
                      <option value="semi_annual" className="bg-[#221F1F]">
                        Semi-Annual
                      </option>
                      <option value="annual" className="bg-[#221F1F]">
                        Annual
                      </option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#A9A3A3] mb-2">
                      Billing Cycle
                    </label>
                    <select
                      value={form.billingCycle}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          billingCycle: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] text-sm focus:outline-none focus:border-green-700 appearance-none cursor-pointer"
                    >
                      <option value="monthly" className="bg-[#221F1F]">
                        Monthly
                      </option>
                      <option value="quarterly" className="bg-[#221F1F]">
                        Quarterly
                      </option>
                      <option value="annual" className="bg-[#221F1F]">
                        Annual
                      </option>
                    </select>
                  </div>
                  <label className="flex items-center gap-3 p-3 rounded-lg bg-[#221F1F] border border-[#504A4A] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.prepaymentRequired}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          prepaymentRequired: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded border-[#504A4A] text-green-700"
                    />
                    <span className="text-sm text-[#EBFFEE]">
                      Require Pre-payment
                    </span>
                  </label>
                  {form.prepaymentRequired && (
                    <div>
                      <label className="block text-xs text-[#A9A3A3] mb-2">
                        Pre-payment Months (0-24)
                      </label>
                      <input
                        type="number"
                        value={form.prepaymentMonths}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            prepaymentMonths: e.target.value,
                          }))
                        }
                        placeholder="e.g., 3"
                        min={1}
                        max={24}
                        className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                      />
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* SECTION 5: Options */}
            <section>
              <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">
                Options
              </h3>
              <div className="space-y-3">
                {form.subscriptionType === 'trial' && (
                  <label className="flex items-center gap-3 p-3 rounded-lg bg-[#221F1F] border border-[#504A4A] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.requiresApproval}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          requiresApproval: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded border-[#504A4A] text-green-700"
                    />
                    <span className="text-sm text-[#EBFFEE]">
                      Requires admin approval
                    </span>
                  </label>
                )}
                <label className="flex items-center gap-3 p-3 rounded-lg bg-[#221F1F] border border-[#504A4A] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.allowsCashPayment}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        allowsCashPayment: e.target.checked,
                      }))
                    }
                    className="w-4 h-4 rounded border-[#504A4A] text-green-700"
                  />
                  <span className="text-sm text-[#EBFFEE]">
                    Allow cash payment
                  </span>
                </label>
              </div>
            </section>

            <div className="p-4 border-t border-[#504A4A] bg-[#1a1818] shrink-0 -mx-4 -mb-4 mt-auto">
              <Button
                type="submit"
                disabled={isPending || (isEditing ? !subscription : !companyId)}
                className="w-full py-3 bg-green-700 hover:bg-green-800 text-white text-base font-semibold disabled:opacity-50"
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2 inline" />
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEditing ? 'Update Subscription' : 'Create Subscription'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
